import ExcelJS from 'exceljs'

// SND-14310: reads an Import Check file in the browser. One row per line, in the order of the check files: Office,
// Invoice, Date, (an empty column), Reference, FoP code, Amount. The empty column is optional; a header row starting
// with "Office" is skipped. CSV, tab- or semicolon-separated text and Excel (.xlsx) are read; text pasted from the
// check report with columns lined up by spaces also works.
export type CheckRow = {
  line: number
  office: string
  invoice: string
  date: string
  extra: string
  reference: string
  fopCode: string
  amount: string
}

function cellText(value: any): string {
  if (value == null) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'object') {
    if ('result' in value) return cellText(value.result)
    if (Array.isArray(value.richText)) return value.richText.map((part: any) => part.text).join('').trim()
    if ('text' in value) return String(value.text).trim()
  }
  return String(value).trim()
}

// One CSV line, with quoted cells ("a, b") and doubled quotes inside them.
function splitDelimited(line: string, delimiter: string) {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (quoted) {
      if (char === '"' && line[index + 1] === '"') {
        cell += '"'
        index += 1
      } else if (char === '"') quoted = false
      else cell += char
    } else if (char === '"') quoted = true
    else if (char === delimiter) {
      cells.push(cell.trim())
      cell = ''
    } else cell += char
  }
  cells.push(cell.trim())
  return cells
}

function toRow(cells: string[], line: number): CheckRow {
  const values = cells.map((cell) => String(cell ?? '').trim())
  const [office, invoice, date, ...rest] = values
  const withGap = rest.length >= 4
  const [extra, reference, fopCode, amount] = withGap ? rest : ['', ...rest]
  return {
    line,
    office: office || '',
    invoice: invoice || '',
    date: date || '',
    extra: extra || '',
    reference: reference || '',
    fopCode: fopCode || '',
    amount: amount || '',
  }
}

function usable(cells: string[]) {
  return cells.some((cell) => String(cell ?? '').trim() !== '')
}

function isHeader(cells: string[]) {
  return /^office/i.test(String(cells[0] ?? '').trim())
}

function readText(content: string): CheckRow[] {
  const lines = content.replace(/^﻿/, '').split(/\r\n|\n|\r/)
  const sample = lines.find((line) => line.trim()) || ''
  const delimiter = sample.includes('\t') ? '\t' : sample.includes(',') ? ',' : sample.includes(';') ? ';' : ''
  const rows: CheckRow[] = []
  lines.forEach((line, index) => {
    const cells = delimiter ? splitDelimited(line, delimiter) : line.trim().split(/\s{2,}/)
    if (!usable(cells) || (rows.length === 0 && isHeader(cells))) return
    rows.push(toRow(cells, index + 1))
  })
  return rows
}

async function readExcel(buffer: ArrayBuffer): Promise<CheckRow[]> {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  const sheet = workbook.worksheets[0]
  if (!sheet) return []
  const rows: CheckRow[] = []
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : []
    const cells = Array.from({ length: values.length }, (_, index) => cellText(values[index]))
    if (!usable(cells) || (rows.length === 0 && isHeader(cells))) return
    rows.push(toRow(cells, rowNumber))
  })
  return rows
}

export async function readCheckFile(file: File): Promise<CheckRow[]> {
  if (/\.xls$/i.test(file.name)) throw new Error('Old .xls files cannot be read: save it as .xlsx or .csv.')
  if (/\.xlsx$/i.test(file.name)) return readExcel(await file.arrayBuffer())
  return readText(await file.text())
}

export async function fileBase64(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(index, index + 0x8000)))
  }
  return window.btoa(binary)
}

// SND-14310 (Rohit, 2026-10-29): when any row has a problem nothing is posted, and the file comes back as Excel with
// the uploaded columns, a Problem column, and every problem row filled red so it can be fixed and uploaded again.
export async function problemFileBlob(rows: CheckRow[], problems: Map<number, string>) {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet('Import Check')
  sheet.addRow(['Office', 'Invoice', 'Date', '', 'Reference', 'FoP', 'Amount', 'Problem']).font = { bold: true }
  for (const row of rows) {
    const problem = problems.get(row.line) || ''
    const added = sheet.addRow([row.office, row.invoice, row.date, row.extra, row.reference, row.fopCode, row.amount, problem])
    if (!problem) continue
    added.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8CBCB' } }
      cell.font = { color: { argb: 'FF9C0006' } }
    })
  }
  sheet.columns.forEach((column, index) => {
    column.width = index === 7 ? 60 : index === 4 ? 24 : 14
  })
  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
